import React from 'react';
import log from 'loglevel';
/**
 * Utilities for processing keyboard input
 */

interface IFocusRestrictor {
    id?: string;
    restrictor: () => void;
}

// A stack of the active focus restrictors, the last element is the restrictor being enforced.
let focusRestrictorStack: IFocusRestrictor[] | null = null;

// The focus event handler, it enforces the most recently created focus rectrictor
const focusRestrictorHandler = () =>
    focusRestrictorStack?.length &&
    focusRestrictorStack[focusRestrictorStack.length - 1].restrictor();

/**
 * Maps Safari Key codes to Keys used in other browsers.
 * Only contains keys used in Sparx Web
 */
const KEY_CODES_TO_KEYS = {
    '13': 'Enter',
    '27': 'Escape',
    '32': ' ',
    '37': 'ArrowLeft',
    '38': 'ArrowUp',
    '39': 'ArrowRight',
    '40': 'ArrowDown',
    '65': 'a',
    '66': 'b',
    '67': 'c',
    '70': 'f',
    '72': 'h',
    '74': 'j',
    '76': 'l',
    '78': 'n',
    '81': 'q',
    '86': 'v',
    '87': 'w',
    '88': 'x',
    '90': 'z',
    '187': '=',
    '189': '-',
};

/**
 * Maps the names of keys in IE and Edge to the names used in Chrome
 * @param keyCode
 * @returns {*}
 */
const KEY_MAPPING = {
    Left: 'ArrowLeft',
    Right: 'ArrowRight',
    Up: 'ArrowUp',
    Down: 'ArrowDown',
    Esc: 'Escape',
};

function keyForKeyCode(keyCode: number): string | null {
    const keyCodeString = keyCode.toString();
    if (!Object.prototype.hasOwnProperty.call(KEY_CODES_TO_KEYS, keyCodeString)) {
        return null;
    }
    return KEY_CODES_TO_KEYS[keyCodeString];
}

/**
 * Given a browser key press event, determine which key was pressed in a way
 * which works for both Safari (which uses keyCode) and other browsers
 * (which use key). (This will only work for keys with a mapping defined in
 * KEY_CODES_TO_KEYS above). Also, IE and Edge use a different name for the
 * arrow keys than Chrome, so we map those names into the Chrome terminology
 * @param keyPressEvent
 * @returns {*} A string specifying which key was pressed - e.g. "Enter"
 */
export function getKeyPressed(keyPressEvent: React.KeyboardEvent | KeyboardEvent) {
    if (keyPressEvent.key) {
        return KEY_MAPPING[keyPressEvent.key] || keyPressEvent.key;
    }
    if (keyPressEvent.keyCode) {
        return keyForKeyCode(keyPressEvent.keyCode);
    }
    return null;
}

/**
 * Used to check if a keypress should trigger the click event for the button
 * that currently has keyboard focus.
 */
export function shouldKeyTrigger(keyPressEvent: React.KeyboardEvent) {
    return getKeyPressed(keyPressEvent) === 'Enter' || getKeyPressed(keyPressEvent) === ' ';
}

/**
 * Add a listener that ensures that keyboard focus cannot leave the given
 * domElement and its children. When focus moves outside the element, it is
 * set back to it. The dom element must have tab order of -1 or a positive
 * number, and it is important to remove the focus restrictor when the element is
 * removed (e.g. in componentDidUnmount) with 'removeKeyboardFocusRestrictor'
 *
 * The most recently created focus restrictor will take priority over any other existing restrictors.
 *
 * @param domElement the dom element to restrict focus within
 * @param elementToFocus the element to place the focus on if it needs to be
 * refocussed
 * @param id a string to identify the focus restrictor, used for debugging
 * @returns {function()} the listener - keep hold of it so it can be removed
 */
export function createKeyboardFocusRestrictor(
    domElement: HTMLElement,
    elementToFocus: HTMLElement,
    id?: string,
) {
    const focusRestrictor = () => {
        // The focussed/active element must be within the given dom element, or
        // we move it back to domElement
        if (
            document.activeElement &&
            document.activeElement !== domElement &&
            !domElement.contains(document.activeElement)
        ) {
            // Focus on the DOM element in the next event loop. Without the
            // timeout it is possible to get a recursive call loop here when
            // one element with a restrictor is disappearing whilst another
            // is appearing - e.g. alert and WAC tutorial
            const focusElement = elementToFocus || domElement;
            setTimeout(() => focusElement.focus(), 0);
        }
    };

    if (focusRestrictorStack === null) {
        // Only register the handler if we haven't before, as denoted by a null stack.
        document.addEventListener('focus', focusRestrictorHandler, true);
        focusRestrictorStack = [];
    }

    focusRestrictorStack.push({
        id: id,
        restrictor: focusRestrictor,
    });
    log.debug(
        `Added focus restrictor for '${id}', Restrictors:`,
        focusRestrictorStack.map(val => val.id),
    );

    return focusRestrictor;
}

/**
 * Removes a previously created keyboard focus restrictor.
 *
 * @param focusRestrictor the restrictor to remove
 */
export function removeKeyboardFocusRestrictor(focusRestrictor: () => void) {
    if (focusRestrictorStack === null) {
        return;
    }
    const idx = focusRestrictorStack?.findIndex(val => val.restrictor === focusRestrictor);
    if (idx !== undefined && idx >= 0) {
        const id = focusRestrictorStack[idx].id;
        focusRestrictorStack.splice(idx, 1);
        log.debug(
            `Removed focus restrictor for '${id}', Restrictors:`,
            focusRestrictorStack.map(val => val.id),
        );
    }
}

/**
 * Determines whether the target of an event is an element with tabIndex >
 * -1 - i.e. can be focussed on by the user
 * @param evt
 * @returns {boolean} true, if the target of the given event is user focussable
 * @private
 */
export function isTargetUserFocussable(evt: any) {
    if (
        evt.target.tabIndex === undefined ||
        evt.target.tabIndex === null ||
        typeof evt.target.tabIndex !== 'number'
    ) {
        return false;
    }

    return evt.target.tabIndex !== -1;
}
